import React from 'react';
import getLogger from '@ignitesales/logger-es';
import areIntlLocalesSupported from 'intl-locales-supported';
import ReactDOM from 'react-dom';
import WebFont from 'webfontloader';
import deepmerge from 'deepmerge';
import { QuestionTypes, QuestionGroups } from './components/index.mjs';
import default_messages_en from './i18n/index.mjs';
import { AppProvider } from './state/index.mjs';
import {setMessages, getLocale, isFunction} from './utils/index.mjs';
import {VALID_APP_CONFIG_KEYS} from './utils/constants.mjs';

const logger = getLogger({ component: 'NeuroApplication' });

/**
 * @typedef GuideConfig
 * @property {Object} [customConfigs] - object of custom values to be made available in the application state
 * @property {Object} [customMessages] - typically contains messages with HTML. Overrides intl messages if a key is present
 * @property {Object} [deepLinking] - if present, guide will attempt to deep link users with path parameter in URL
 * @property {Object} [errorValidations] - configuration options for how to render errors on survey pages
 * @property {Object} [htmlMessageValues] - html message values used for parsing within the FormattedMessage component
 * @property {boolean} [isDialogue] - indicates the guide is running on Dialogue platform
 * @property {Boolean} [isDemoMode] - if true, neuro components will consider this mode
 * @property {Object} [salesforceConfig] - config options for Salesforce use
 * @property {Object} [questionLayouts] - question layout options
 * @property {Object} [questionTypes] - question types
 * @property {Object} [uiBreakpoints] - breakpoint provider values
 * @property {Object} [urlParamAnswers] - mapping of param keys to appkey strings that should be saved from URL query
 * @property {Object} [uiConfig] - ui configuration option that may be used in the app
 *
 * @typedef LocaleConfig
 * @property {string} [locale] - current lang locale
 * @property {Array} [supportedLocales] - locales available for app
 */
/**
 *
 * @typedef NeuroInitOptions
 * @property {React.Component} AppComponent - application component
 * @property {React.Component} AppLoadingComponent - loading component shown prior to app render
 * @property {GuideConfig} [guideConfig] - guide specific configuration options
 * @property {HTMLElement} [htmlElement] - DOM element where app will be rendered
 * @property {LocaleConfig} localeConfig - i18n config options for messages, lang, etc.
 * @property {Object} messages - application i18n messages
 * @property {Function} onLoginEmployee - custom login function that overrides default enableLoginFlow function. Application will send object param that includes dispatch and employee info if available
 * @property {Function} onStateInit - function that will be called to decorate the state immediately services init state
 * @property {Array} [reducers] - custom reducers for the app
 * @property {Number} [loadDelay] - amount of time to delay before loading application state when using mock mode, default is 2000ms
 */
const defaultOptions = {
  AppComponent: null,
  AppLoadingComponent: null,
  guideConfig: {
    customMessages: null,
    htmlMessageValues: {},
    questionLayouts: QuestionGroups,
    questionTypes: QuestionTypes,
    isDemoMode: false
  },
  htmlElement: null,
  localeConfig: {
    locale: 'en',
    supportedLocales: ['en']
  },
  messages: null,
  onLoginEmployee: null,
  onStateInit: null,
  reducers: [],
  loadDelay: 2000
};

/**
 * Neuro application initialization function
 *
 * @param {NeuroInitOptions} options
 * @returns {Promise<boolean>}
 */
const init = async (options) => {
  logger.info('Initializing Ignite Neuro application');

  WebFont.load({
    google: {
      families: ['Source Sans Pro:200,300,300i,400,400i,600,600i,700,700i,900'],
    },
  });

  /**
   *
   * @type {NeuroInitOptions}
   * @private
   */
  const _opts = deepmerge( defaultOptions, options );
  _opts.localeConfig.locale = getLocale(_opts.localeConfig.supportedLocales);

  const isRunningDemoMode = _opts.guideConfig.isDemoMode;
  const locale = _opts.localeConfig.locale;
  const htmlElement = _opts.htmlElement ? _opts.htmlElement : document.getElementById('root');
  const { supportedLocales } = _opts.localeConfig;
  const { customMessages, htmlMessageValues, questionLayouts, questionTypes, ...otherConfigurations } = _opts.guideConfig;
  const filteredConfigurations = Object.fromEntries(
    Object.entries(otherConfigurations || {}).filter( ([key]) => VALID_APP_CONFIG_KEYS.includes(key) )
  );

  const bundleMessages = {
    ..._opts.messages,
    en: _opts?.messages?.en
      ? { ...default_messages_en.en, ..._opts.messages.en }
      : default_messages_en.en,
  };

  /* eslint-disable-next-line no-undef */
  await ignite.servicesInitialized();

  // Assign custom login function to the window, so it can be called from LoginActions method
  if( isFunction(_opts.onLoginEmployee) ) {
    window.ignite.onLoginEmployee = _opts.onLoginEmployee;
  }

  //load messages from window variable if set in demo mode
  let demoMessages = {};
  if(isRunningDemoMode && window?.demoMode) {
    logger.debug('Fetching demoMode configuration to use', window?.demoMode);
    demoMessages = window.demoMode?.messages || {};
  }

  /* eslint-disable-next-line no-undef */
  const serverMessages = await ignite.pageResolutionService.loadMessages();
  const messages = {
    [locale]: {
      ...(bundleMessages[locale] || {}),
      ...(serverMessages || {}),
      ...(demoMessages || {})
    }
  };
  setMessages(messages);

  if (!areIntlLocalesSupported(supportedLocales)) {
    const childLog = logger.child({ supportedLocales });
    childLog.info('importing locale data');
    const promises = [
      //import('intl-pluralrules'), //TODO: investigate if we need pluralrules still???
      import('@formatjs/intl-relativetimeformat/polyfill'),
    ];
    supportedLocales.forEach((locale) => {
      promises.push(
        import('@formatjs/intl-relativetimeformat/dist/locale-data/' + locale)
      );
    });

    await Promise.all(promises);
    childLog.info('Completed importing locale data');
  }

  ReactDOM.render(
    <AppProvider
      AppComponent={_opts.AppComponent}
      AppLoadingComponent={_opts.AppLoadingComponent}
      configurations={filteredConfigurations}
      customMessages={customMessages}
      htmlMessageValues={htmlMessageValues}
      locale={_opts.localeConfig.locale}
      messages={messages}
      questionGroupTypes={{ ...defaultOptions.guideConfig.questionLayouts, ...questionLayouts }}
      questionTypes={{ ...defaultOptions.guideConfig.questionTypes, ...questionTypes }}
      onStateInit={_opts.onStateInit}
      reducers={_opts.reducers}
      timeout={_opts.loadDelay}
    />,
    htmlElement
  );

  logger.info('neuro initialization complete');
};

/**
 * @deprecated since version 3.0
 */
const initializeNeuroApplication = init;

export {
  init as initializeNeuroGuide,
  initializeNeuroApplication
};
export default init;
